import { t } from "i18next";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

/**
 * Unbuilt, so a workflow can compose rules of its own onto these questions. A
 * new builder on every call: the builder is mutable, so a shared instance would
 * carry one workflow's rules into every other.
 */
export const informationForHealthcareFormBuilder = () =>
  new MultiPageFormBuilder()
    .withFieldAlwaysVisible({
      fieldName: "incidentDescription", // 136-healthinfo-1.0
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      label: tk("informationForHealthcare.incidentDescription"),
      multiline: true,
      rows: "5",
    })
    .withFieldAlwaysVisible({
      fieldName: "wasHealthcareProvided", // 136-healthinfo-2.0
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("informationForHealthcare.wasHealthcareProvided"),
      options: [
        {
          label: tk("common.yes"),
          value: "yes",
        },
        {
          label: tk("common.no"),
          value: "no",
        },
      ],
    })
    .withReadonlyFieldAlwaysVisible({
      fieldName: "healthcareBanner", // 136-healthinfo-2.0.2
      type: "banner",
      body: tk("informationForHealthcare.healthcareBanner"),
      bannerType: "warning",
    })
    .withFieldConditionallyVisible({
      fieldName: "medicalAttentionProvided", // 136-healthinfo-2.0.1
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.wasHealthcareProvided?.value === "yes",
      label: tk("informationForHealthcare.medicalAttentionProvided"),
      multiline: true,
      rows: "5",
    })
    .withFieldAlwaysVisible({
      fieldName: "wasForceUsed", // 136-healthinfo-3.0
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("informationForHealthcare.wasForceUsed"),
      options: [
        {
          label: tk("common.yes"),
          value: "yes",
        },
        {
          label: tk("common.no"),
          value: "no",
        },
      ],
    })
    /**
     * Nullable rather than requiring a selection, so that a record written before
     * this question existed still parses against the current definition. Such a
     * record can have answered that force was used without ever being shown this.
     */
    .withFieldConditionallyVisible({
      fieldName: "useOfForceType", // 136-healthinfo-3.2
      type: "multi-select",
      schema: z.array(valueAndLabelSchemaDeprecated(z.string())).nullable(),
      isVisible: ({ formValues }) => formValues.wasForceUsed?.value === "yes",
      label: tk("informationForHealthcare.useOfForceType.title"),
      options: [
        {
          value: "baton",
          label: tk("informationForHealthcare.useOfForceType.options.baton"),
        },
        {
          value: "tacticalCommunication",
          label: tk(
            "informationForHealthcare.useOfForceType.options.tacticalCommunication",
          ),
        },
        {
          value: "irritantSpray",
          label: tk(
            "informationForHealthcare.useOfForceType.options.irritantSpray",
          ),
        },
        {
          value: "dogs",
          label: tk("informationForHealthcare.useOfForceType.options.dogs"),
        },
        {
          value: "dogBite",
          label: tk("informationForHealthcare.useOfForceType.options.dogBite"),
        },
        {
          value: "groundRestraint",
          label: tk(
            "informationForHealthcare.useOfForceType.options.groundRestraint",
          ),
        },
        {
          value: "handcuffingNonCompliant",
          label: tk(
            "informationForHealthcare.useOfForceType.options.handcuffingNonCompliant",
          ),
        },
        {
          value: "handcuffingCompliant",
          label: tk(
            "informationForHealthcare.useOfForceType.options.handcuffingCompliant",
          ),
        },
        {
          value: "limbBodyRestraints",
          label: tk(
            "informationForHealthcare.useOfForceType.options.limbBodyRestraints",
          ),
        },
        {
          value: "shield",
          label: tk("informationForHealthcare.useOfForceType.options.shield"),
        },
        {
          value: "spitGuard",
          label: tk(
            "informationForHealthcare.useOfForceType.options.spitGuard",
          ),
        },
        {
          value: "ced",
          label: tk("informationForHealthcare.useOfForceType.options.ced"),
        },
        {
          value: "unarmedSkills",
          label: tk(
            "informationForHealthcare.useOfForceType.options.unarmedSkills",
          ),
        },
        {
          value: "otherImprovised",
          label: tk(
            "informationForHealthcare.useOfForceType.options.otherImprovised",
          ),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextBaton",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(({ value }) => value === "baton") !==
        undefined,
      label: tk("informationForHealthcare.useOfForceContextBaton.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextBaton.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextBatonOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextBaton?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextTacticalCommunication",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "tacticalCommunication",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextTacticalCommunication.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextTacticalCommunication.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextTacticalCommunicationOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextTacticalCommunication?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextIrritantSpray",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "irritantSpray",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextIrritantSpray.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextIrritantSpray.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextIrritantSprayOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextIrritantSpray?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextDogs",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(({ value }) => value === "dogs") !==
        undefined,
      label: tk("informationForHealthcare.useOfForceContextDogs.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextDogs.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextDogsOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextDogs?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextDogBite",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(({ value }) => value === "dogBite") !==
        undefined,
      label: tk("informationForHealthcare.useOfForceContextDogBite.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextDogBite.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextDogBiteOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextDogBite?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextGroundRestraint",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "groundRestraint",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextGroundRestraint.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextGroundRestraint.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextGroundRestraintOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextGroundRestraint?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextHandcuffingNonCompliant",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "handcuffingNonCompliant",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextHandcuffingNonCompliant.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextHandcuffingNonCompliant.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextHandcuffingNonCompliantOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextHandcuffingNonCompliant?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextHandcuffingCompliant",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "handcuffingCompliant",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextHandcuffingCompliant.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextHandcuffingCompliant.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextHandcuffingCompliantOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextHandcuffingCompliant?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextLimbBodyRestraints",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "limbBodyRestraints",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextLimbBodyRestraints.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextLimbBodyRestraints.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextLimbBodyRestraintsOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextLimbBodyRestraints?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextShield",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(({ value }) => value === "shield") !==
        undefined,
      label: tk("informationForHealthcare.useOfForceContextShield.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextShield.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextShieldOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextShield?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextSpitGuard",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "spitGuard",
        ) !== undefined,
      label: tk("informationForHealthcare.useOfForceContextSpitGuard.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextSpitGuard.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextSpitGuardOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextSpitGuard?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextCed",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(({ value }) => value === "ced") !==
        undefined,
      label: tk("informationForHealthcare.useOfForceContextCed.title"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextCed.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextCedOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextCed?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextUnarmedSkills",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "unarmedSkills",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextUnarmedSkills.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextUnarmedSkills.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextUnarmedSkillsOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextUnarmedSkills?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextOtherImprovised",
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(
          z.enum([
            "immediatelyOnScene" as const,
            "enrouteToPlaceOfSafety" as const,
            "intermittentlyAtPlaceOfSafety" as const,
            "consistentlyAtPlaceOfSafety" as const,
            "detentionUntilHandover" as const,
            "other" as const,
          ]),
        )
        .captureValueLabel()
        .build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceType?.find(
          ({ value }) => value === "otherImprovised",
        ) !== undefined,
      label: tk(
        "informationForHealthcare.useOfForceContextOtherImprovised.title",
      ),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherImprovised.required",
      ),
      options: [
        {
          value: "immediatelyOnScene",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.immediatelyOnScene",
          ),
        },
        {
          value: "enrouteToPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.enrouteToPlaceOfSafety",
          ),
        },
        {
          value: "intermittentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.intermittentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "consistentlyAtPlaceOfSafety",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.consistentlyAtPlaceOfSafety",
          ),
        },
        {
          value: "detentionUntilHandover",
          label: tk(
            "informationForHealthcare.useOfForceContext.options.detentionUntilHandover",
          ),
        },
        {
          value: "other",
          label: tk("informationForHealthcare.useOfForceContext.options.other"),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "useOfForceContextOtherImprovisedOther",
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.useOfForceContextOtherImprovised?.value === "other",
      label: tk("informationForHealthcare.useOfForceContextOther"),
      requiredLabel: tk(
        "informationForHealthcare.useOfForceContextOtherRequired",
      ),
      multiline: true,
      rows: "5",
    })
    .withFieldAlwaysVisible({
      fieldName: "hasPersonBeenSearched", // 136-healthinfo-4.0
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("informationForHealthcare.hasPersonBeenSearched"),
      options: [
        {
          label: tk("common.yes"),
          value: "yes",
        },
        {
          label: tk("common.no"),
          value: "no",
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "didSeizeProperty", // 136-healthinfo-5.0
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      isVisible({ formValues }) {
        return formValues.hasPersonBeenSearched?.value === "yes";
      },
      label: tk("informationForHealthcare.didSeizeProperty"),
      options: [
        {
          label: tk("common.yes"),
          value: "yes",
        },
        {
          label: tk("common.no"),
          value: "no",
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "detailsPropertySeized", // 136-healthinfo-5.0.1
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.didSeizeProperty?.value === "yes",
      label: tk("informationForHealthcare.detailsPropertySeized"),
      multiline: true,
      rows: "5",
    })
    .withFieldAlwaysVisible({
      fieldName: "hasAdditionalRiskFactors", // 136-healthinfo-7.0
      type: "radio",
      ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
      label: tk("informationForHealthcare.hasAdditionalRiskFactors"),
      options: [
        {
          label: tk("common.yes"),
          value: "yes",
        },
        {
          label: tk("common.no"),
          value: "no",
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "describeRiskFactors", // 136-healthinfo-7.0.1
      type: "multi-select",
      schema: z
        .array(valueAndLabelSchemaDeprecated(z.string()))
        .min(1, "At least one option must be selected."),
      isVisible: ({ formValues }) =>
        formValues.hasAdditionalRiskFactors?.value === "yes",
      label: tk("informationForHealthcare.describeRiskFactors.title"),
      options: [
        {
          value: "substanceAbuse",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.substanceAbuse",
          ),
        },
        {
          value: "bereavement",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.bereavement",
          ),
        },
        {
          value: "bullying",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.bullying",
          ),
        },
        {
          value: "carerIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.carerIssues",
          ),
        },
        {
          value: "clininicallyDiagnosedNeurodiverse",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedNeurodiverse",
          ),
        },
        {
          value: "clininicallyDiagnosedMentalHealthIllness",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedMentalHealthIllness",
          ),
        },
        {
          value: "clininicallyDiagnosedIllness",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.clininicallyDiagnosedIllness",
          ),
        },
        {
          value: "disability",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.disability",
          ),
        },
        {
          value: "domesticAbuse",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.domesticAbuse",
          ),
        },
        {
          value: "stress",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.stress",
          ),
        },
        {
          value: "financialIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.financialIssues",
          ),
        },
        {
          value: "genderIdentityIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.genderIdentityIssues",
          ),
        },
        {
          value: "traumaHistory",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.traumaHistory",
          ),
        },
        {
          value: "housingIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.housingIssues",
          ),
        },
        {
          value: "militaryHistory",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.militaryHistory",
          ),
        },
        {
          value: "natalConcerns",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.natalConcerns",
          ),
        },
        {
          value: "previousConvictions",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.previousConvictions",
          ),
        },
        {
          value: "previouslySectioned",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.previouslySectioned",
          ),
        },
        {
          value: "relationshipIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.relationshipIssues",
          ),
        },
        {
          value: "sexualOrientationIssues",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.sexualOrientationIssues",
          ),
        },
        {
          value: "suspectedSeriousCrime",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.suspectedSeriousCrime",
          ),
        },
        {
          value: "other",
          label: tk(
            "informationForHealthcare.describeRiskFactors.options.other",
          ),
        },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "describeRiskFactorsOther", // 136-healthinfo-7.0.1.1.1
      type: "textbox",
      ...new FieldBuilder().withSchema(longTextSchema).build(),
      isVisible: ({ formValues }) =>
        formValues.describeRiskFactors?.find(
          ({ value }) => value === "other",
        ) !== undefined,
      label: tk("informationForHealthcare.describeRiskFactorsOther"),
      multiline: true,
      rows: "5",
    })
    .withPage([
      "incidentDescription", // 136-healthinfo-1.0
    ])
    .withPage([
      "wasHealthcareProvided", // 136-healthinfo-2.0
      "healthcareBanner", // 136-healthinfo-2.0.2
      "medicalAttentionProvided", // 136-healthinfo-2.0.1
    ])
    .withPage([
      "wasForceUsed", // 136-healthinfo-3.0
    ])
    .withPage([
      "useOfForceType", // 136-healthinfo-3.2
    ])
    .withPage(["useOfForceContextBaton", "useOfForceContextBatonOther"])
    .withPage([
      "useOfForceContextTacticalCommunication",
      "useOfForceContextTacticalCommunicationOther",
    ])
    .withPage([
      "useOfForceContextIrritantSpray",
      "useOfForceContextIrritantSprayOther",
    ])
    .withPage(["useOfForceContextDogs", "useOfForceContextDogsOther"])
    .withPage(["useOfForceContextDogBite", "useOfForceContextDogBiteOther"])
    .withPage([
      "useOfForceContextGroundRestraint",
      "useOfForceContextGroundRestraintOther",
    ])
    .withPage([
      "useOfForceContextHandcuffingNonCompliant",
      "useOfForceContextHandcuffingNonCompliantOther",
    ])
    .withPage([
      "useOfForceContextHandcuffingCompliant",
      "useOfForceContextHandcuffingCompliantOther",
    ])
    .withPage([
      "useOfForceContextLimbBodyRestraints",
      "useOfForceContextLimbBodyRestraintsOther",
    ])
    .withPage(["useOfForceContextShield", "useOfForceContextShieldOther"])
    .withPage(["useOfForceContextSpitGuard", "useOfForceContextSpitGuardOther"])
    .withPage(["useOfForceContextCed", "useOfForceContextCedOther"])
    .withPage([
      "useOfForceContextUnarmedSkills",
      "useOfForceContextUnarmedSkillsOther",
    ])
    .withPage([
      "useOfForceContextOtherImprovised",
      "useOfForceContextOtherImprovisedOther",
    ])
    .withPage([
      "hasPersonBeenSearched", // 136-healthinfo-4.0
      "didSeizeProperty", // 136-healthinfo-5.0
      "detailsPropertySeized", // 136-healthinfo-5.0.1
    ])
    .withPage([
      "hasAdditionalRiskFactors", // 136-healthinfo-7.0
      "describeRiskFactors", // 136-healthinfo-7.0.1
      "describeRiskFactorsOther", // 136-healthinfo-7.0.1.1.1
    ]);

export const formSpecification = informationForHealthcareFormBuilder().build({
  t,
});

export type InformationForHealthcareFormData = ReturnType<
  typeof formSpecification
>["formType"];
